import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import API from '../config/api';

export default function Orders() {
  const { token, user } = useContext(AuthContext);
  const { lang, t, formatPrice } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(API.orders.userOrders, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (!res.ok || data.status !== 'success') {
          throw new Error(data.message || 'Unable to load your orders.');
        }
        setOrders(Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        setError(err.message || 'Unable to load orders.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const emptyState = useMemo(() => {
    if (!user) return lang === 'de' ? t('orders.emptyState.notLoggedIn') : 'Please log in to view your orders.';
    return lang === 'de' ? t('orders.emptyState.noOrders') : 'No orders found for this account yet.';
  }, [user, lang, t]);

  const statusChip = (status) => {
    const map = {
      pending: { bg: 'rgba(245,158,11,0.12)', color: '#c37a00', label: lang === 'de' ? 'ausstehend' : 'pending' },
      paid: { bg: 'rgba(59,130,246,0.12)', color: '#1d4ed8', label: lang === 'de' ? 'bezahlt' : 'paid' },
      shipped: { bg: 'rgba(139,92,246,0.12)', color: '#6d28d9', label: lang === 'de' ? 'versandt' : 'shipped' },
      delivered: { bg: 'rgba(16,185,129,0.14)', color: '#047857', label: lang === 'de' ? 'zugestellt' : 'delivered' },
      cancelled: { bg: 'rgba(239,68,68,0.14)', color: '#b91c1c', label: lang === 'de' ? 'storniert' : 'cancelled' },
      failed: { bg: 'rgba(239,68,68,0.14)', color: '#b91c1c', label: lang === 'de' ? 'fehlgeschlagen' : 'failed' },
    };
    return map[status] || { bg: 'rgba(120,120,120,0.14)', color: '#555', label: status };
  };

  return (
    <main className="kh-orders-page" style={{ background: '#f6f5f1', minHeight: '100vh', padding: '56px 20px' }}>
      <section style={{ maxWidth: '1080px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ margin: 0, fontFamily: 'Noto Serif, Georgia, serif', fontWeight: 400, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', color: '#1f1d16' }}>{lang === 'de' ? t('orders.title') : 'My Orders'}</h1>
          <p style={{ margin: '10px 0 0', color: '#666055', fontSize: '14px' }}>{lang === 'de' ? t('orders.subtitle') : 'Track placed orders, payment status, and upcoming delivery updates.'}</p>
        </div>

        {!token && (
          <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '22px' }}>
            <p style={{ margin: '0 0 14px', color: '#5f5a4f' }}>{emptyState}</p>
            <Link to="/login" className="btn-primary">{lang === 'de' ? t('orders.loginToContinue') : 'Login To Continue'}</Link>
          </div>
        )}

        {token && loading && (
          <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '22px', color: '#6a665d' }}>
            {lang === 'de' ? 'Lade Ihre Bestellungen...' : 'Loading your orders...'}
          </div>
        )}

        {token && !loading && error && (
          <div style={{ background: '#fff2f2', border: '1px solid #f5c2c2', borderRadius: '12px', padding: '16px', color: '#9c1d1d' }}>
            {error}
          </div>
        )}

        {token && !loading && !error && orders.length === 0 && (
          <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '22px' }}>
            <p style={{ margin: '0 0 12px', color: '#5f5a4f' }}>{emptyState}</p>
            <Link to="/shop" className="btn-primary">{lang === 'de' ? t('cart.browseCollection') : 'Browse Collection'}</Link>
          </div>
        )}

        {token && !loading && !error && orders.length > 0 && (
          <div style={{ display: 'grid', gap: '14px' }}>
            {orders.map((order) => {
              const chip = statusChip(order.status);
              return (
                <article key={order.id} className="kh-order-card" style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '18px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '16px', color: '#1f1d16' }}>{order.order_public_id ? `${lang === 'de' ? 'Bestellung' : 'Order'} #${order.order_public_id}` : `${lang === 'de' ? 'Bestellung' : 'Order'} #${order.id}`}</h3>
                      <p style={{ margin: '6px 0 0', color: '#666055', fontSize: '13px' }}>{new Date(order.created_at).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US')}</p>
                    </div>
                    <span style={{ padding: '6px 10px', borderRadius: '999px', background: chip.bg, color: chip.color, fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                      {chip.label}
                    </span>
                  </div>

                  <div className="kh-order-card-meta" style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#8c877d', textTransform: 'uppercase', letterSpacing: '1px' }}>{lang === 'de' ? t('cart.total') : 'Total'}</p>
                      <p style={{ margin: 0, color: '#1f1d16', fontWeight: 600 }}>{formatPrice(order.total_amount).display}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#8c877d', textTransform: 'uppercase', letterSpacing: '1px' }}>{lang === 'de' ? t('orders.table.items') : 'Items'}</p>
                      <p style={{ margin: 0, color: '#1f1d16', fontWeight: 600 }}>{order.item_count || 0}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#8c877d', textTransform: 'uppercase', letterSpacing: '1px' }}>{lang === 'de' ? 'Sendungsverfolgung' : 'Tracking'}</p>
                      <p style={{ margin: 0, color: '#1f1d16', fontWeight: 600 }}>{order.tracking_number || (lang === 'de' ? 'Wird nach Versand mitgeteilt' : 'Will be shared after dispatch')}</p>
                    </div>
                  </div>

                  <div className="kh-order-card-actions" style={{ marginTop: '14px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {order.order_public_id ? (
                      <Link to={`/orders/${encodeURIComponent(order.order_public_id)}`} style={{ textDecoration: 'none', padding: '9px 14px', borderRadius: '7px', border: '1px solid #d4c7a9', color: '#7d5e12', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                        {lang === 'de' ? t('orders.viewBill') : 'View Bill'}
                      </Link>
                    ) : (
                      <span style={{ padding: '9px 14px', borderRadius: '7px', border: '1px solid #e0d8c6', color: '#9b907a', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                        {lang === 'de' ? 'Rechnung nicht verfügbar' : 'Bill Unavailable'}
                      </span>
                    )}
                    {order.tracking_url && (
                      <a href={order.tracking_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', padding: '9px 14px', borderRadius: '7px', border: '1px solid #d7d7d7', color: '#3f3f3f', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                        {lang === 'de' ? 'Sendung verfolgen' : 'Track Shipment'}
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      <style>{`
        @media (max-width: 700px) {
          .kh-orders-page { padding: 28px 12px !important; }
          .kh-order-card { padding: 14px !important; }
          .kh-order-card-meta { grid-template-columns: 1fr !important; gap: 8px !important; }
          .kh-order-card-actions > a,
          .kh-order-card-actions > span {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </main>
  );
}
